import React, { useCallback, useMemo, useState } from "react";
import { reconServices } from "../recon-services";
import { ReconFileSummaryType, ReconLogEntryType, ReconReportStatusType } from "./types";
import { Moment } from "moment";
import moment from "moment";
import ConfirmationModal from "../../../../components/reusable-ui/confirmation-modal";
import { RECON_DATA, recon_logs_default_limit } from "../constants";
import history from "../../../../history";
import { DOWNLOADS_PAGE_ROUTE } from "../../../../data/routes-constants";
import { getUpdatedUserDetails } from "../../../../helpers/user-info-helpers";
import { DATE_TIME_FORMATS } from "../../../../data/constants";
import { enumerateDaysBetweenDatesNew } from "../../../../helpers/moment-helper";
import store from "../../../../redux/store";

export type AnalyticsDataType = {
  _id: string;
  totalOrders: number;
  totalOrdersAmount: number;
  totalPayoutDifferences: number;
  payoutDifference: number;
  matchedOrderCount: number;
  unmatchedOrderCount: number;
  notFoundOrderCount: number;
};
export type AnalyticsDataKeys = keyof AnalyticsDataType;
export type ReportRowDataType = ReconReportStatusType & { fileWiseSummary: ReconFileSummaryType; links?: { name: string; url: string; } };

export enum ReportsType {
  done,
  pending
}
export type FilterDataType = { applied: boolean; branchIds?: string[]; dateRange: [Moment | null, Moment | null] | null; aggregator?: 'zomato' | "swiggy" | ""[] };
export type ReconStatusType = {
  name: string; message: string;
}
type ReconContextDataType = {
  uploadFileModal: boolean;
  reconLogsModal: {
    modal: boolean;
    loading: boolean;
    data: {
      totalFiles: number;
      files: ReconLogEntryType[];
    }
  };
  filterData: FilterDataType;
  analyticsData?: AnalyticsDataType;
  reportsViewData: {
    loading: boolean;
    selectedOutlets: string[];
    uploadedReports: { files: ReportRowDataType[]; totalFiles: number; };
    reports: { files: ReconFileSummaryType[]; totalFiles: number; }
    pendingReports: ReconReportStatusType[];
    reportType: ReportsType
  };
  detailedReportData?: ReportRowDataType;
  selectedReportWithoutFilter?: Array<string>
}
type ReconContextType = {
  reconContextData: ReconContextDataType;
  updateReconContext: (data: ReconContextDataType) => void;
  toggleUploadModal: () => void;
  toggleSelectAllReports: (val: boolean) => void;
  selectSingleReport: (mid: string, type?: boolean) => void;
  getPendingReportsData: () => void;
  fetchCompletedReportsData: () => void;
  fetchReconSummary: () => void;
  resetFilters: () => void;
  applyFilters: () => void;
  setReportViewType: (type: ReportsType) => void;
  downloadAndEmailReport: (download: boolean, outletId?: string, isFilterApplied?: boolean) => void;
  updateFilterData: (newData: Omit<FilterDataType, 'applied'>) => void;
  fetchDefaultData: () => void;
  toggleReconLogs: () => void;
  fetchReconLogs: (limit: number, page: number) => void;
  fetchUploadedFiles: (limit: number, page: number) => void;
  fetchOutletWiseReports: (limit: number, page: number) => void;
}
const defaultFilterData = { branchIds: [], dateRange: null, aggregator: [], applied: false } as FilterDataType;
export const defaultTimePeriodMonths = 6;
const defaultLogsData = { limit: 5, files: [], totalFiles: 0 };
const defaultSelectDate: [Moment, Moment] = [moment().subtract(defaultTimePeriodMonths, "months"), moment()];
const defaultReconContextData: ReconContextDataType = {
  uploadFileModal: false,
  reconLogsModal: {
    modal: false,
    loading: false,
    data: defaultLogsData,
  },
  analyticsData: undefined,
  reportsViewData: {
    loading: false,
    selectedOutlets: [],
    uploadedReports: { files: [], totalFiles: 0 },
    reports: { files: [], totalFiles: 0 },
    pendingReports: [],
    reportType: ReportsType.done
  },
  detailedReportData: undefined,
  filterData: defaultFilterData,
  selectedReportWithoutFilter: []
}
export const ReconContext = React.createContext<ReconContextType>({
  reconContextData: defaultReconContextData,
  fetchDefaultData: () => { },
  updateReconContext: () => { },
  toggleUploadModal: () => { },
  toggleSelectAllReports: (val: boolean) => { },
  selectSingleReport: () => { },
  fetchCompletedReportsData: () => { },
  getPendingReportsData: () => { },
  fetchReconSummary: () => { },
  resetFilters: () => { },
  applyFilters: () => { },
  setReportViewType: () => { },
  downloadAndEmailReport: () => { },
  updateFilterData: () => { },
  toggleReconLogs: () => { },
  fetchReconLogs: () => { },
  fetchUploadedFiles: () => { },
  fetchOutletWiseReports: () => { },
});

const ReconEngineContextProvider = ({
  children,
}: React.PropsWithChildren<{}>) => {
  const [data, setData] = useState<ReconContextDataType>(
    defaultReconContextData
  );
  const [downloadReportState, setDownloadReportState] = useState<{ show: boolean; mode: 'email' | 'download' }>({ show: false, mode: 'email' });
  const currentUserDetail = useMemo(getUpdatedUserDetails, []);
  const toggleUploadModal = useCallback(function () {
    setData((data) => {
      return { ...data, uploadFileModal: !data.uploadFileModal };
    });
  }, [setData]);

  const toggleSelectAllReports = useCallback(function (state: boolean) {
    setData((data) => {
      const isFilterApplied = data.filterData?.applied
      const updatedSelectedOutlets = state ? data.reportsViewData.reports.files.map(report => report.outletId) : [];
      const selectedFilesForUnfilteredData = state ? data.reportsViewData?.uploadedReports?.files?.map(report => report?.attachment?.id) : []
      return { ...data, ...(isFilterApplied ? { reportsViewData: { ...data.reportsViewData, selectedOutlets: updatedSelectedOutlets } } : { selectedReportWithoutFilter: selectedFilesForUnfilteredData }) };
    });
  }, [setData]);

  async function getPendingReportsData() {
    // let reports: ReconReportStatusType[] = [];
    // try {
    //   const result = await reconServices.fetchPendingReports(data.filterData) as ReconReportStatusType[];
    //   reports = result;
    // }
    // catch (e) {
    //   console.log('error in fetching', e);
    // }
    // setData((data) => { return { ...data, reportsViewData: { ...data.reportsViewData, pendingReports: reports, loading: false } } });
  }

  async function fetchCompletedReportsData() {
    // setData({ ...data, reportsViewData: { ...data.reportsViewData, loading: true, reportType: ReportsType.done } });
    // let reports: ReportRowDataType[] = [];
    // try {
    //   const result = await reconServices.fetchReconReports(data.filterData) as ReportRowDataType[];
    //   reports = result;
    // }
    // catch (e) {
    //   console.log('error in fetching', e);
    // }
    // setData((data) => { return { ...data, reportsViewData: { ...data.reportsViewData, reports, loading: false } } });
  }

  const applyFilters = useCallback(async function () {
    setData((data) => { return { ...data, filterData: { ...data.filterData, applied: true }, reportsViewData: { ...data.reportsViewData, loading: true, reportType: ReportsType.done } } });
    //api call
    try {
      const result = await reconServices.fetchReconSummary({ ...data.filterData, page: 1, limit: recon_logs_default_limit }, true) as { summary: AnalyticsDataType[]; files: ReconFileSummaryType[]; totalFiles: number };
      setData((data) => { return { ...data, analyticsData: result.summary?.[0], selectedReportWithoutFilter: [], reportsViewData: { ...data.reportsViewData, selectedOutlets: [], reportType: ReportsType.done, loading: false, reports: { files: result.files, totalFiles: result.totalFiles } } } });
    }
    catch (e) {
      setData((data) => { return { ...data, analyticsData: undefined, selectedReportWithoutFilter: [], reportsViewData: { ...data.reportsViewData, selectedOutlets: [], reportType: ReportsType.done, loading: false, reports: { files: [], totalFiles: 0 } } } });
    }
    }, [data])

  const resetFilters = useCallback(async function () {
    fetchDefaultData();
    setData((data) => { return { ...data, reportsViewData: { ...data.reportsViewData, selectedOutlets: [] } } }); //reset selected outlets
  }, [data])

  const setReportViewType = useCallback((type: ReportsType) => {
    setData({ ...data, reportsViewData: { ...data.reportsViewData, reportType: type } });
  }, [data, setData]);

  const selectSingleReport = useCallback(function (customId: string, isFilterApplied = true) {
    if (!isFilterApplied) {
      setData((data) => {
        const updatedSelectedReport = [...data.selectedReportWithoutFilter || []];
        if (updatedSelectedReport?.includes(customId)) {
          const removeIndex = updatedSelectedReport.findIndex((id) => customId === id);
          updatedSelectedReport.splice(removeIndex, 1);
        }
        else {
          updatedSelectedReport.push(customId);
        }
        return { ...data, selectedReportWithoutFilter: updatedSelectedReport }
      })
    }
    else {
      setData((data) => {
        const updatedOutlets = [...data.reportsViewData.selectedOutlets];
        if (updatedOutlets.includes(customId)) {
          const removeIndex = updatedOutlets.findIndex((id) => customId === id);
          updatedOutlets.splice(removeIndex, 1);
        }
        else {
          updatedOutlets.push(customId);
        }
        return { ...data, reportsViewData: { ...data.reportsViewData, selectedOutlets: updatedOutlets } };
      });
    }
  }, [setData]);

  async function fetchReconSummary() {
    // try {
    //   const analyticsData = await reconServices.fetchReconSummary(data.filterData) as AnalyticsDataType;
    //   setData((data) => {
    //     return { ...data, analyticsData }
    //   });
    // } catch (e) {
    // }
  }

  function updateFilterData(newData: Omit<FilterDataType, 'applied'>) {
    setData((data) => { return { ...data, filterData: { ...data.filterData, ...newData } } });
  }

  const fetchDefaultData = useCallback(async () => {
    //summary
    const defaultData: FilterDataType = { applied: false, dateRange: defaultSelectDate };
    // remove unused analytics data api call
    // const analyticsData = await reconServices.fetchReconSummary({ ...defaultData, page: 1, limit: recon_logs_default_limit }) as AnalyticsDataType;
    setData((data) => { return { ...data, filterData: defaultFilterData } });
    fetchUploadedFiles(recon_logs_default_limit, 1);
  }, [])

  const fetchUploadedFiles = useCallback(async (limit: number, page: number) => {
    setData((data) => { return { ...data, reportsViewData: { ...data.reportsViewData, loading: true, reportType: ReportsType.done } } });
    const uploadedFiles = await reconServices.fetchUploadedFiles({ limit, page }) as { files: ReportRowDataType[]; totalFiles: number; }
    setData((data) => { return { ...data, filterData: defaultFilterData, reportsViewData: { ...data.reportsViewData, loading: false, uploadedReports: uploadedFiles } } });
  }, []);

  const fetchOutletWiseReports = useCallback(async (limit: number, page: number) => {
    setData((data) => {
      return {
        ...data,
        reportsViewData: { ...data.reportsViewData, loading: true }
      }
    });
    const result = await reconServices.fetchReconSummary({ ...data.filterData, page, limit }, true) as { summary: AnalyticsDataType[]; files: ReconFileSummaryType[]; totalFiles: number };
    setData((data) => {
      return {
        ...data,
        reportsViewData: { ...data.reportsViewData, loading: false, reports: { files: result.files, totalFiles: result.totalFiles } }
      }
    });
  }, [data.filterData]);

  const toggleReconLogs = useCallback(async () => {
    if (data.reconLogsModal.modal) {
      setData(({ ...data, reconLogsModal: { data: defaultLogsData, loading: false, modal: false } }));
      return;
    }
    fetchReconLogs(recon_logs_default_limit, 1);
  }, [data.reconLogsModal])

  const fetchReconLogs = useCallback(async (limit: number, page: number) => {
    setData((data) => ({ ...data, reconLogsModal: { ...data.reconLogsModal, loading: true } }));
    const logsResponse: { files: ReconLogEntryType[]; totalFiles: number } = await reconServices.fetchReconLogs({ limit, page }) as { files: ReconLogEntryType[]; totalFiles: number };
    setData((data) => ({ ...data, reconLogsModal: { data: logsResponse, loading: false, modal: true } }));
  }, [])

  //if we get outlet id in parameter send that, otherwise send selectedOutletIds
  const downloadAndEmailReport = useCallback(async (download: boolean, outletId?: string, isSingleReport = false) => {
    const {
      businessReducer: { userData: { permittedBranches } },
    } = store.getState();
    const permittedBranchIds = permittedBranches.map((branch: any) => branch.branchId);
    const periods = enumerateDaysBetweenDatesNew(data.filterData?.dateRange?.[0] as Moment, data.filterData?.dateRange?.[1] as Moment, DATE_TIME_FORMATS.yearMonthDay);
    const payload = {
      periods,
      branchIds: data.filterData?.branchIds?.length ? data.filterData?.branchIds : permittedBranchIds || [],
      outletIds: outletId ? [outletId] : data.reportsViewData.selectedOutlets,
      saveReport: download
    };

    const payloadForUnfilteredReports = {
      branchIds: data.filterData?.branchIds?.length ? data.filterData?.branchIds : permittedBranchIds || [],
      attachmentIds: isSingleReport ? [outletId || ''] :  data.selectedReportWithoutFilter,
      saveReport: download
    }

    const updatedPayload = data.filterData.applied ? payload : payloadForUnfilteredReports;
    try {
      const downloadResponse = await reconServices.downloadReconSummary(updatedPayload);
      console.log(downloadResponse);
      setDownloadReportState({ show: true, mode: download ? 'download' : 'email' });
    }
    catch(err) {
      console.log(err);
    }
  }, [data.reportsViewData.selectedOutlets, data.filterData, data.selectedReportWithoutFilter]);

  function resetDownloadModal() {
    setDownloadReportState({ show: false, mode: 'email' });
  }

  function handleRedirectDownload() {
    history.push(DOWNLOADS_PAGE_ROUTE);
  }

  const value = useMemo(
    () => ({
      reconContextData: data,
      updateReconContext: setData,
      toggleUploadModal,
      toggleSelectAllReports,
      selectSingleReport,
      getPendingReportsData,
      fetchCompletedReportsData,
      fetchReconSummary,
      resetFilters,
      applyFilters,
      setReportViewType,
      downloadAndEmailReport,
      updateFilterData,
      fetchDefaultData,
      toggleReconLogs,
      fetchReconLogs,
      fetchUploadedFiles,
      fetchOutletWiseReports
    }),
    [data]
  );
  return (
    <>
      <ReconContext.Provider value={value}>{children}</ReconContext.Provider>
      {downloadReportState.show && <ConfirmationModal
        headingText={downloadReportState.mode === 'download' ? RECON_DATA.download.downloadDoneHeader : RECON_DATA.download.emailSentHeader}
        description={downloadReportState.mode === 'download' ? RECON_DATA.download.downloadDoneInfo : RECON_DATA.download.emailDoneInfo(currentUserDetail.email || currentUserDetail.verifiedEmail)}
        onModalClose={resetDownloadModal}
        onConfirmBtnClick={handleRedirectDownload}
        customClass="w-[500px] pt-[26px]"
        showCloseIcon={false}
        iconName={downloadReportState.mode === 'download' ? 'recon-download-icon' : 'recon-email-icon'}
        ctaProps={{ cancelText: downloadReportState.mode === 'download' ? "Check Later" : "Got It", confirmText: "Go To Downloads Section" }}
      />}
    </>
  );
}

export default ReconEngineContextProvider;